// src/modules/app-views/collaborators/pages/Init/modals/CollaboratorsParametersModal/tabs/PositionsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import PositionFormModal from './PositionFormModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PositionsTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for positions - initialize from store, update locally after save
  const [positions, setPositions] = useState(() => {
    const rawPositions = appService.getModulePreferences({ category: 'positions' });
    return normalizePositions(rawPositions);
  });

  // Filter out removed positions for display in table
  const visiblePositions = useMemo(() => positions.filter(p => !p.removed), [positions]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      // Check for duplicates (excluding current item if editing, and excluding removed)
      const isDuplicate = positions.some(
        p =>
          p.uid !== editingRecord?.uid &&
          !p.removed &&
          p.label.toLowerCase() === formData.label.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedPositions;

        if (editingRecord) {
          // Editing existing position - keep the same uid and preserve removed flag
          updatedPositions = positions.map(p =>
            p.uid === editingRecord.uid
              ? { ...p, label: formData.label }
              : p
          );
        } else {
          // Adding new position - generate new uid
          updatedPositions = [
            ...positions,
            {
              uid: generateUidV4(),
              label: formData.label,
            },
          ];
        }

        // Save array directly: positions: [...]
        await appService.updateModulePreferences({
          category: 'positions',
          data: updatedPositions,
        });

        // Update local state immediately (don't wait for SSE)
        setPositions(updatedPositions);

        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
      } catch (error) {
        console.error('[PositionsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [positions, editingRecord, setLoading]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        // Hard delete: remove from array
        const updatedPositions = positions.filter(p => p.uid !== record.uid);
        appService.message({ flag: 's', text: 'removed_successfully' });

        // Save array directly: positions: [...]
        await appService.updateModulePreferences({
          category: 'positions',
          data: updatedPositions,
        });

        // Update local state immediately (don't wait for SSE)
        setPositions(updatedPositions);
      } catch (error) {
        console.error('[PositionsTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [positions, setLoading]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('position'),
      dataIndex: 'label',
      render: text => <span className="font-weight-bold">{text}</span>,
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visiblePositions}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_position')}
        </Button>
      </Flex>

      <PositionFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
        mousePosition={mousePosition}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PositionsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizePositions(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizePosition);
}

function normalizePosition(position) {
  return {
    uid: position.uid,
    label: position.label || '',
    ...(position.removed && { removed: true }),
  };
}
